// Field-line tracing + display rendering.
//
// Owns the three operations that mutate viewer.objects.fieldlines.lines:
//   - traceFromSelection()  trace a streamline starting at the selection cube
//   - clearAll()            remove and dispose every traced line
//   - applyDisplay()        re-render existing lines as tubes/Lines and apply opacity
//
// `traceFromSelection` offloads to the worker when ready; otherwise falls back
// to fieldlineCore.traceSync (which lives on viewer.objects.fieldlines.core,
// rebuilt in switchDataset/switchFieldComponent).
//
// Note: scene → ui:task:show/hide intents below are the allowed direction.
// Long-running scene work needs to notify UI overlays; routing through the
// store keeps the direction strict (scene never imports a UI module) and the
// UI side can pick up the signal via any driver (panel or headless).
//
// params contract — keys read from the reactive store:
//   showFieldLines, fieldLineStep, fieldLineMaxSteps
//   fieldLineTubes, fieldLineRadius, fieldLineMaterial, fieldLineOpacity
//   fieldLineDepthWrite, fieldLineDepthTest, fieldLineRenderOrder
//   fieldLineCastShadow, fieldLineReceiveShadow

import * as THREE from 'three';
import { physicalToThreeJS, threeJSToPhysical } from '@adapters/coordinates';
import { isFieldlineReady, traceFieldLineAsync } from '../workers/fieldlineClient';
import { store } from '../store/instance';
import { FIELDLINE_DEFAULTS } from '../config/defaults';
import { INTENTS } from '../store/intents';
import { debugLog, debugWarn } from '@adapters/debug';
import { resolveDomainBounds, isOutsideDomain } from '../store/selections';
import type { ViewerContext } from '../app/context';
import type { ParamsState } from '../store/schema';

interface FieldlineCore {
    isOutsideBounds?: (x: number, y: number, z: number) => boolean;
    traceSync: (start: { x: number; y: number; z: number }, h: number, maxSteps: number) => Array<{ x: number; y: number; z: number }>;
}

interface SeedLike {
    x: number; y: number; z: number;
    step?: number;
    maxSteps?: number;
}

interface GroupUserData {
    centerlinePoints?: THREE.Vector3[];
    [key: string]: unknown;
}

const showTask = (msg: string): void => { store.dispatch(INTENTS.UI_TASK_SHOW, { msg }); };
const hideTask = (): void => { store.dispatch(INTENTS.UI_TASK_HIDE); };

function buildLineGroup(viewer: ViewerContext, scenePoints: THREE.Vector3[], params: ParamsState): THREE.Group {
    const fc = viewer.sceneMeta.fieldlineConfig;
    const lineGeom = new THREE.BufferGeometry().setFromPoints(scenePoints);
    const lineMat = new THREE.LineBasicMaterial({ color: fc.color, linewidth: 2, transparent: fc.opacity < 1.0, opacity: fc.opacity });
    const line = new THREE.Line(lineGeom, lineMat);
    const sphereGeom = new THREE.SphereGeometry(0.1, 12, 12);
    const startMat = new THREE.MeshBasicMaterial({ color: 0xff6666 });
    const endMat = new THREE.MeshBasicMaterial({ color: 0x66ccff });
    const startSp = new THREE.Mesh(sphereGeom, startMat);
    const endSp = new THREE.Mesh(sphereGeom, endMat);
    if (scenePoints.length > 0) {
        const first = scenePoints[0];
        const last = scenePoints[scenePoints.length - 1];
        if (first) startSp.position.copy(first);
        if (last) endSp.position.copy(last);
    }
    const group = new THREE.Group();
    group.name = `FieldLine_${Date.now()}`;
    group.add(line); group.add(startSp); group.add(endSp);
    try { (group.userData as GroupUserData).centerlinePoints = scenePoints.map((p) => p.clone()); } catch { /* swallow */ }

    if (scenePoints.length >= 2) {
        const segLen: number[] = []; let totalLen = 0;
        for (let i = 0; i < scenePoints.length - 1; i++) {
            const a = scenePoints[i]; const b = scenePoints[i + 1];
            if (!a || !b) continue;
            const d = b.distanceTo(a);
            segLen.push(d); totalLen += d;
        }
        const addArrowAtFraction = (frac: number): void => {
            if (totalLen <= 0) return;
            const target = totalLen * frac; let acc = 0;
            for (let i = 0; i < segLen.length; i++) {
                const d = segLen[i] ?? 0;
                if (acc + d >= target && d > 0) {
                    const t = (target - acc) / d;
                    const a = scenePoints[i]; const b = scenePoints[i + 1];
                    if (!a || !b) return;
                    const pos = a.clone().lerp(b, t);
                    const tangent = b.clone().sub(a);
                    if (tangent.lengthSq() <= 1e-12) return;
                    const arrowLen = fc.coneLength;
                    const arrowRad = fc.coneRadius;
                    const coneGeom = new THREE.ConeGeometry(arrowRad, arrowLen, 20);
                    const gridHex = (viewer.theme.current && viewer.theme.current.scene && viewer.theme.current.scene.grid && viewer.theme.current.scene.grid.color)
                        ? new THREE.Color(viewer.theme.current.scene.grid.color).getHex()
                        : 0xffffff;
                    const coneHex: number | string = (fc.coneColor !== null) ? fc.coneColor : gridHex;
                    const coneMat = new THREE.MeshBasicMaterial({ color: coneHex, transparent: fc.coneOpacity < 1.0, opacity: fc.coneOpacity, depthWrite: false });
                    const cone = new THREE.Mesh(coneGeom, coneMat);
                    const tangentNorm = tangent.normalize();
                    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), tangentNorm);
                    cone.quaternion.copy(quat); cone.position.copy(pos);
                    group.add(cone);
                    return;
                }
                acc += d;
            }
        };
        addArrowAtFraction(0.25);
        addArrowAtFraction(0.75);
    }
    group.visible = (params.showFieldLines !== false);
    return group;
}

/**
 * Trace a field line from the selection cube position. Async path uses the
 * fieldline worker; sync fallback uses viewer.objects.fieldlines.core.
 */
export function traceFromSelection(viewer: ViewerContext, params: ParamsState): void {
    if (!viewer.objects.selection.invisibleMesh) {
        debugWarn(viewer, 'Trace: selection cube not initialized');
        return;
    }
    const centerThree = viewer.objects.selection.invisibleMesh.position.clone();
    const start = threeJSToPhysical(centerThree.x, centerThree.y, centerThree.z);
    const h = params.fieldLineStep || 0.1;
    const maxSteps = params.fieldLineMaxSteps || 5000;
    const fieldData = viewer.field.data;
    const BxArr = fieldData ? fieldData['Bx'] : undefined;
    const ByArr = fieldData ? fieldData['By'] : undefined;
    const BzArr = fieldData ? fieldData['Bz'] : undefined;
    if (!BxArr || !ByArr || !BzArr) {
        debugWarn(viewer, 'Trace: Bx/By/Bz not available in current dataset; cannot trace field line.');
        return;
    }
    debugLog(viewer, 'Trace: start', start.x.toFixed(3), start.y.toFixed(3), start.z.toFixed(3), 'step=', h, 'maxSteps=', maxSteps);
    const core = viewer.objects.fieldlines.core as FieldlineCore | null;
    const outOfBounds = core && core.isOutsideBounds
        ? core.isOutsideBounds(start.x, start.y, start.z)
        : (start.x < viewer.grid.xMin || start.x > viewer.grid.xMax ||
           start.y < viewer.grid.yMin || start.y > viewer.grid.yMax ||
           start.z < viewer.grid.zMin || start.z > viewer.grid.zMax);
    if (outOfBounds) {
        debugWarn(viewer, 'Trace: start is outside grid bounds; aborting');
        return;
    }
    // Stage 5 — resolve the field-line domain so the worker (or sync
    // fallback) terminates the trace at the selection boundary.
    const domain = resolveDomainBounds(params.selections, params.fieldLinesDomain);
    if (domain.kind !== 'full' && isOutsideDomain(start.x, start.y, start.z, domain)) {
        debugWarn(viewer, 'Trace: start is outside selection domain; aborting');
        return;
    }

    const scene = viewer.three.scene;
    if (!scene) return;

    if (isFieldlineReady) {
        try {
            showTask('Tracing field line…');
            const startPayload = { x: start.x, y: start.y, z: start.z };
            const run = async (): Promise<void> => {
                try {
                    const pts = await traceFieldLineAsync(startPayload, h, maxSteps, domain);
                    hideTask();
                    if (!pts || !pts.length) return;
                    const scenePoints: THREE.Vector3[] = [];
                    for (let i = 0; i < pts.length; i += 3) {
                        scenePoints.push(physicalToThreeJS(pts[i] ?? 0, pts[i + 1] ?? 0, pts[i + 2] ?? 0));
                    }
                    const group = buildLineGroup(viewer, scenePoints, params);
                    scene.add(group);
                    viewer.objects.fieldlines.lines.push(group);
                    try { applyDisplay(viewer, params); } catch { /* swallow */ }
                } catch { hideTask(); }
            };
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => { void run(); });
            else setTimeout(() => { void run(); }, 0);
            return;
        } catch { hideTask(); /* fall through to sync */ }
    }

    let combinedFiltered = core ? core.traceSync(start, h, maxSteps) : [];
    if (domain.kind !== 'full' && combinedFiltered.length) {
        // Trim at the first point outside the domain — keeps the sync-path
        // semantics aligned with the worker's "terminate on exit" behaviour.
        const trimmed: Array<{ x: number; y: number; z: number }> = [];
        for (const pt of combinedFiltered) {
            if (isOutsideDomain(pt.x, pt.y, pt.z, domain)) break;
            trimmed.push(pt);
        }
        combinedFiltered = trimmed;
    }
    if (!combinedFiltered.length) {
        debugWarn(viewer, 'Trace: no points generated');
        return;
    }
    const scenePoints = combinedFiltered.map((v) => physicalToThreeJS(v.x, v.y, v.z));
    const group = buildLineGroup(viewer, scenePoints, params);
    scene.add(group);
    viewer.objects.fieldlines.lines.push(group);
    try { applyDisplay(viewer, params); } catch { /* swallow */ }
    debugLog(viewer, 'Trace: field line added to scene as group', group.name);
}

interface SeedFromConfigOpts {
    showProgress?: boolean;
    onAfterEach?: ((group: THREE.Group) => void) | null;
}

/**
 * Trace each seed in viewer.objects.fieldlines.seeds and add its line group
 * to the scene.
 */
export async function seedFromConfig(viewer: ViewerContext, params: ParamsState, opts: SeedFromConfigOpts = {}): Promise<void> {
    const { showProgress = false, onAfterEach = null } = opts;
    const seeds = viewer.objects.fieldlines.seeds as unknown as SeedLike[];
    if (!Array.isArray(seeds) || seeds.length === 0) return;
    const scene = viewer.three.scene;
    if (!scene) return;
    if (showProgress) showTask('Tracing field lines…');
    try {
        for (const seed of seeds) {
            const start = { x: seed.x, y: seed.y, z: seed.z };
            const h = seed.step || params.fieldLineStep || 0.1;
            const maxSteps = seed.maxSteps || params.fieldLineMaxSteps || 5000;
            const pts = await traceFieldLineAsync(start, h, maxSteps);
            if (!pts || !pts.length) continue;
            const scenePoints: THREE.Vector3[] = [];
            for (let i = 0; i < pts.length; i += 3) {
                scenePoints.push(physicalToThreeJS(pts[i] ?? 0, pts[i + 1] ?? 0, pts[i + 2] ?? 0));
            }
            const group = buildLineGroup(viewer, scenePoints, params);
            scene.add(group);
            viewer.objects.fieldlines.lines.push(group);
            store.dispatch(INTENTS.SET_FIELDLINE_DISPLAY, { rebuildGeometry: true });
            if (typeof onAfterEach === 'function') onAfterEach(group);
        }
    } finally {
        if (showProgress) hideTask();
    }
}

/** Remove and dispose every traced field line group. */
export function clearAll(viewer: ViewerContext): void {
    const lines = viewer.objects.fieldlines.lines;
    if (!Array.isArray(lines) || lines.length === 0) return;
    lines.forEach((g) => {
        try {
            if (!g) return;
            if (g.parent) g.parent.remove(g);
            g.traverse((obj) => {
                const mesh = obj as THREE.Mesh;
                if (mesh.geometry && mesh.geometry.dispose) mesh.geometry.dispose();
                if (mesh.material) {
                    if (Array.isArray(mesh.material)) mesh.material.forEach((m) => { if (m && m.dispose) m.dispose(); });
                    else if (mesh.material.dispose) mesh.material.dispose();
                }
            });
        } catch { /* swallow */ }
    });
    lines.length = 0;
    debugLog(viewer, 'Cleared all field lines');
}

interface ApplyDisplayOpts {
    rebuildGeometry?: boolean;
}

/**
 * Re-render existing field lines per current display params (tubes vs lines,
 * opacity, radius, depth flags, render order, shadows).
 */
export function applyDisplay(viewer: ViewerContext, params: ParamsState, opts: ApplyDisplayOpts = {}): void {
    const { rebuildGeometry = false } = opts;
    try {
        const useTubes = (typeof params.fieldLineTubes === 'undefined') ? !!FIELDLINE_DEFAULTS.tubes : !!params.fieldLineTubes;
        const alpha = (typeof params.fieldLineOpacity === 'number') ? params.fieldLineOpacity : 1.0;
        const radius = (typeof params.fieldLineRadius === 'number') ? params.fieldLineRadius : 0.03;
        const matType = params.fieldLineMaterial || 'standard';
        const depthWrite = !!params.fieldLineDepthWrite;
        const depthTest = !!params.fieldLineDepthTest;
        const renderOrder = (typeof params.fieldLineRenderOrder === 'number') ? params.fieldLineRenderOrder : 0;
        const castShadow = !!params.fieldLineCastShadow;
        const receiveShadow = !!params.fieldLineReceiveShadow;
        viewer.objects.fieldlines.lines.forEach((groupObj) => {
            if (!groupObj) return;
            const group = groupObj as THREE.Group;
            const children = group.children ? group.children.slice() : [];
            const colorHex = viewer.sceneMeta.fieldlineConfig.color || 0xffcc66;
            const userData = group.userData as GroupUserData;

            let points: THREE.Vector3[] | null = (userData && Array.isArray(userData.centerlinePoints)) ? userData.centerlinePoints : null;
            if (!points) {
                const lineChild = children.find((c): c is THREE.Line => (c as THREE.Line).isLine === true);
                if (lineChild && lineChild.geometry && lineChild.geometry.attributes['position']) {
                    const pos = lineChild.geometry.attributes['position']; const arr: THREE.Vector3[] = [];
                    for (let i = 0; i < pos.count; i++) arr.push(new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)));
                    points = arr;
                } else {
                    const tubeChild = children.find((c): c is THREE.Mesh => (c as THREE.Mesh).isMesh === true && (c as THREE.Mesh).geometry?.type === 'TubeGeometry');
                    const tubeGeomAny = tubeChild?.geometry as (THREE.TubeGeometry | undefined);
                    const path = tubeGeomAny?.parameters?.path;
                    const tubularSegments = tubeGeomAny?.parameters?.tubularSegments || 64;
                    if (path && typeof path.getPoints === 'function') points = path.getPoints(tubularSegments);
                }
                if (points) {
                    try { userData.centerlinePoints = points.map((p) => p.clone()); } catch { /* swallow */ }
                }
            }

            if (useTubes) {
                children.forEach((child) => {
                    if ((child as THREE.Line).isLine === true) {
                        group.remove(child);
                        const line = child as THREE.Line;
                        if (line.geometry && line.geometry.dispose) line.geometry.dispose();
                        const m = Array.isArray(line.material) ? line.material[0] : line.material;
                        if (m && m.dispose) m.dispose();
                    }
                });
                let tubeChild = group.children.find((c): c is THREE.Mesh => (c as THREE.Mesh).isMesh === true && (c as THREE.Mesh).geometry?.type === 'TubeGeometry');
                if (!tubeChild && points && points.length >= 2) {
                    const path = new THREE.CurvePath<THREE.Vector3>();
                    for (let i = 0; i < points.length - 1; i++) {
                        const a = points[i]; const b = points[i + 1];
                        if (a && b) path.add(new THREE.LineCurve3(a, b));
                    }
                    const tubularSegments = Math.max(points.length - 1, 1);
                    const tubeGeom = new THREE.TubeGeometry(path as unknown as THREE.Curve<THREE.Vector3>, tubularSegments, radius, 8, false);
                    const mat = (matType === 'lambert')
                        ? new THREE.MeshLambertMaterial({ color: colorHex, transparent: alpha < 1.0, opacity: alpha })
                        : (matType === 'phong')
                            ? new THREE.MeshPhongMaterial({ color: colorHex, transparent: alpha < 1.0, opacity: alpha, shininess: 30 })
                            : new THREE.MeshStandardMaterial({ color: colorHex, transparent: alpha < 1.0, opacity: alpha, metalness: 0.05, roughness: 0.85 });
                    mat.depthWrite = depthWrite; mat.depthTest = depthTest;
                    const tube = new THREE.Mesh(tubeGeom, mat);
                    tube.renderOrder = renderOrder;
                    tube.castShadow = castShadow; tube.receiveShadow = receiveShadow;
                    group.add(tube);
                    tubeChild = tube;
                } else if (tubeChild && points && points.length >= 2) {
                    const tubeGeomAny = tubeChild.geometry as THREE.TubeGeometry;
                    if (rebuildGeometry || Math.abs(tubeGeomAny.parameters.radius - radius) > 1e-6) {
                        const path = new THREE.CurvePath<THREE.Vector3>();
                        for (let i = 0; i < points.length - 1; i++) {
                            const a = points[i]; const b = points[i + 1];
                            if (a && b) path.add(new THREE.LineCurve3(a, b));
                        }
                        const tubularSegments = Math.max(points.length - 1, 1);
                        const newGeom = new THREE.TubeGeometry(path as unknown as THREE.Curve<THREE.Vector3>, tubularSegments, radius, 8, false);
                        const oldGeom = tubeChild.geometry; tubeChild.geometry = newGeom; if (oldGeom && oldGeom.dispose) oldGeom.dispose();
                    }
                }
            } else {
                children.forEach((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh === true && mesh.geometry?.type === 'TubeGeometry') {
                        group.remove(mesh);
                        if (mesh.geometry && mesh.geometry.dispose) mesh.geometry.dispose();
                        const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
                        if (m && m.dispose) m.dispose();
                    }
                });
                const hasLine = children.some((c) => (c as THREE.Line).isLine === true);
                if (!hasLine && points && points.length >= 2) {
                    const lineGeom = new THREE.BufferGeometry().setFromPoints(points);
                    const lineMat = new THREE.LineBasicMaterial({ color: colorHex, transparent: alpha < 1.0, opacity: alpha });
                    const line = new THREE.Line(lineGeom, lineMat);
                    group.add(line);
                }
            }

            group.children.forEach((child) => {
                const mesh = child as THREE.Mesh;
                const matRaw = mesh.material;
                const mat = Array.isArray(matRaw) ? matRaw[0] : matRaw;
                if (mat) {
                    mat.opacity = alpha;
                    mat.transparent = alpha < 1.0;
                    if (mesh.isMesh === true) {
                        mat.depthWrite = depthWrite;
                        mat.depthTest = depthTest;
                        mesh.renderOrder = renderOrder;
                        mesh.castShadow = castShadow;
                        mesh.receiveShadow = receiveShadow;
                    }
                    mat.needsUpdate = true;
                }
            });
        });
    } catch { /* swallow */ }
}
